import { DownloadOutlined, UploadOutlined } from '@ant-design5/icons';
import { Checkbox, Col, Divider, Row, Upload } from 'antd5';
import { isEmpty, orderBy, uniqBy } from 'lodash';
import { type MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useCSVDownloader } from 'react-papaparse';
import styled from 'styled-components';

import { useUploadContext } from 'components/FileUploader';
import { Button, Collapse, TooltipWithQuestionIcon, useMessage } from 'components/ui';
import {
  type AtlasGqlValidateImageMetadataFileInput,
  AtlasGqlDecimalSeparator,
  AtlasGqlFileFieldDelimiter,
  AtlasGqlFileFormat,
  useGetImageMetadataFieldsQuery,
  useValidateImageMetadataFileMutation,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import {
  AUTONOMOUS_DRONE,
  BLADE_INTERNAL,
  CSV_BULK_UPLOAD_OPTIONS,
  IMAGE_AND_METADATA_UPLOAD_OPTIONS,
  JSON_BULK_UPLOAD_OPTIONS,
  MANUAL_DRONE,
} from 'utils/constants';
import { uploadIngestFiles, useGetIngestUploadUrls } from 'utils/ingest';
import { AERO_ENTERPRISE_UPLOAD, SKYCRAWLER_UPLOAD } from 'utils/release-toggles';

import { getBulkUploadEventTypes } from '../../utils';
import { type ExampleImageDetails, EXAMPLE_IMAGE_DETAILS } from '../constants';
import { useUploadInspectionDataModalContext } from '../Context/UploadInspectionDataModalContext';
import { FieldMappingForm } from '../FieldMappingForm';
import {
  type CloseOnSuccessProps,
  type InspectionUploadWorkflowComponentProps,
  Steps,
  UploadTypes,
} from '../UploadInspectionDataModal';
import { ButtonContainer } from '../UploadInspectionDataModal.style';
import { flatParseJSON, getProvidedFieldNamesFromJsonRecords, parseCsv } from '../utils';

import { AeroEnterpriseUpload } from './AeroEnterpriseUpload';
import { Preview } from './Preview';
import { RoboticsUpload } from './RoboticsUpload';
import { type ImageDetailsValidationProps, Validation } from './Validation';

type ImageDetailsProps = Omit<
  InspectionUploadWorkflowComponentProps,
  'setValidationStepProps' | 'setCreatedInspectionIds' | 'setCreatedTaskIds'
> & {
  initialStep?: Steps;
  inspectionIds: string[];
  uploadType: UploadTypes;
  onCloseModal: () => void;
  onProcessComplete: (input: CloseOnSuccessProps) => void;
  validateImageMetadataInputRef: MutableRefObject<
    AtlasGqlValidateImageMetadataFileInput | undefined
  >;
};

export const ImageDetails = ({
  requireTaskId,
  step,
  setStep,
  initialStep,
  inspectionIds,
  inspectionTasks,
  uploadType,
  uploadId,
  onCloseModal,
  onProcessComplete,
  onComplete,
  validateImageMetadataInputRef,
}: ImageDetailsProps) => {
  const { hasReleaseToggle, features } = useAccountContext();
  const hasSkyCrawlerUploadRT = hasReleaseToggle(SKYCRAWLER_UPLOAD);
  const hasAeroEnterpriseUploadRT = hasReleaseToggle(AERO_ENTERPRISE_UPLOAD);
  const {
    SKYCRAWLER_UPLOAD: hasSkyCrawlerUploadFlag,
    AERO_ENTERPRISE_UPLOAD: hasAeroEnterpriseUploadFlag,
    ROBOTICS_UPLOAD: hasRoboticsUploadFlag,
  } = features;

  const { setAlertState, setErroredUploadSteps, imageDetailsContext, inspectionType } =
    useUploadInspectionDataModalContext();
  const {
    file,
    setFile,
    fieldMappings,
    setFieldMappings,
    previewData,
    setPreviewData,
    setImagePathFieldName,
    imageDetailsFileType: fileType,
    setImageDetailsFileType: setFileType,
  } = imageDetailsContext;

  const [providedFieldNames, setProvidedFieldNames] = useState<string[]>();
  const [inspectionUploadEventTypes, setInspectionUploadEventTypes] = useState(() =>
    getBulkUploadEventTypes(CSV_BULK_UPLOAD_OPTIONS.CSV_BULK)
  );
  const [likelyDelimiterError, setLikelyDelimiterError] = useState<boolean>(false);
  const [cellDelimiter, setCellDelimiter] = useState<AtlasGqlFileFieldDelimiter>();
  const [decimalDelimiter, setDecimalDelimiter] = useState<AtlasGqlDecimalSeparator>(
    AtlasGqlDecimalSeparator.Period
  );
  const [validationStepProps, setValidationStepProps] = useState<ImageDetailsValidationProps>();

  /**
   * TODO: SC-125542
   *   - state for validation step
   *   - call props.setErroredUploadSteps(uploadErrors => ({...uploadErrors, imageDetails: TODO}))
   *     as appropriate if validation has errors
   */

  const { message } = useMessage();

  const { data: imageMetadataFieldsData } = useGetImageMetadataFieldsQuery({
    variables: inspectionType?.id ? { inspectionTypeId: inspectionType.id } : undefined,
  });
  const imageMetadataFields = orderBy(
    imageMetadataFieldsData?.imageMetadataFields ?? [],
    'defaultOrder'
  );

  const internalFields = useMemo(() => {
    const fields = uniqBy(imageMetadataFields, 'internalName');

    if (requireTaskId) {
      return fields.map(field =>
        field.internalName === 'taskId' ? { ...field, required: true } : field
      );
    }

    return fields;
  }, [imageMetadataFields, requireTaskId]);

  const existingTaskCount = inspectionTasks?.length ?? 0;

  const { exampleCsv, exampleJson } = useMemo(() => {
    if (internalFields.length === 0) {
      return { exampleCsv: '', exampleJson: '' };
    }

    let exampleImageDetails: ExampleImageDetails[] = EXAMPLE_IMAGE_DETAILS;

    if (inspectionTasks && existingTaskCount) {
      const existingTaskIds: string[] = inspectionTasks.map(({ taskId }) => taskId);

      exampleImageDetails = existingTaskIds.map((taskId, i) => ({
        id: '',
        taskId,
        path: `path/to/image${i}`,
        imageUrl: `https://example.com/image${i}`,
        blade: '',
        bladeChamber: '',
        bladeSide: '',
        radialDistance: '',
        height: '',
        direction: '',
      }));
    }

    const csvDeleteNote = 'DELETE THIS ROW BEFORE UPLOADING';
    const exampleCsv =
      'Notes,' +
      internalFields.map(field => field.defaultCsvHeader ?? field.internalName).join(',') +
      '\n' +
      `Explanation: ${csvDeleteNote},` +
      internalFields.map(field => `"${field.explanation?.replace(/\"/g, '""') ?? ''}"`).join(',') +
      '\n' +
      `Valid Data Options: ${csvDeleteNote},` +
      internalFields.map(field => `"${field.validOptions?.replace(/\"/g, '""') ?? ''}"`).join(',') +
      '\n' +
      exampleImageDetails
        .map(
          (details, i) =>
            `Example ${i + 1}: ${csvDeleteNote},` +
            internalFields
              .map(field => details[field.internalName as keyof typeof details])
              .join(',')
        )
        .join('\n');

    const jsonDeleteNote = 'DELETE THIS ENTRY BEFORE UPLOADING';
    const exampleData = [
      Object.fromEntries([
        ['Notes', `Explanation and Valid Data Options: ${jsonDeleteNote}`],
        ...internalFields.map(field => [
          field.defaultJsonFieldName ?? field.internalName,
          `${field.explanation ?? ''}${field.validOptions ? ` ${field.validOptions}` : ''}`,
        ]),
      ]),
      ...exampleImageDetails.map((details, i) =>
        Object.fromEntries([
          ['Notes', `Example ${i + 1}: ${jsonDeleteNote}`],
          ...internalFields.map(field => [
            field.defaultJsonFieldName ?? field.internalName,
            details[field.internalName as keyof typeof details],
          ]),
        ])
      ),
    ];
    const exampleJson = `data:application/json,${JSON.stringify(exampleData)}`;

    return { exampleCsv, exampleJson };
  }, [internalFields, inspectionTasks, existingTaskCount]);

  const parseFile = useCallback(
    async (file: File) => {
      if (file.type === 'text/csv') {
        const csvText = await file.text();

        const { meta, data, delimiter, likelyDelimiterError, error } = parseCsv(
          csvText,
          cellDelimiter
        );

        if (error) {
          setAlertState({
            alertDescription: error,
            alertType: 'error',
          });
        } else {
          setAlertState(null);
          setProvidedFieldNames(meta.fields.filter(field => field !== 'Notes'));
          setFileType(AtlasGqlFileFormat.Csv);
          setCellDelimiter(delimiter);
          setLikelyDelimiterError(likelyDelimiterError);
          setPreviewData(data);
        }
      } else if (file.type === 'application/json') {
        const jsonText = await file.text();

        try {
          const parsedJson = flatParseJSON(jsonText);
          /**
           * This assumes (for now) that all records are the same format and are
           * simple key/value pairs. Based on the requirements, this seems fine
           * for now (especially since we have an example file). We may want to support
           * more options in the future.
           */
          if (isEmpty(parsedJson)) {
            setAlertState({
              alertDescription: 'File contained no data. Please check your file and reupload.',
              alertType: 'error',
            });
          } else if (Array.isArray(parsedJson) && parsedJson.length) {
            setProvidedFieldNames(getProvidedFieldNamesFromJsonRecords(parsedJson));
            setFileType(AtlasGqlFileFormat.Json);
            setPreviewData(parsedJson);
          }
        } catch (error) {
          message.error('Error parsing JSON. Please verify formatting and try again.');
        }
      } else {
        message.error('File type not supported. Please upload a .csv or .json file.');
      }
    },
    [cellDelimiter]
  );

  const handleUpload = ({ file }: { file: File; fileList: File[] }) => {
    setFile(file);
    parseFile(file);
  };

  useEffect(() => {
    if (file) {
      parseFile(file);
    }
  }, [cellDelimiter]);

  useEffect(() => {
    const inspectionType = {
      [AtlasGqlFileFormat.Csv]: CSV_BULK_UPLOAD_OPTIONS.CSV_BULK,
      [AtlasGqlFileFormat.Json]: JSON_BULK_UPLOAD_OPTIONS.JSON_BULK,
    }[fileType];

    setInspectionUploadEventTypes(() => getBulkUploadEventTypes(inspectionType));
  }, [fileType]);

  useEffect(() => {
    if (providedFieldNames?.length && step !== Steps.DetailsPreview) {
      setStep(Steps.DetailsMapping);
    }
  }, [providedFieldNames]);

  useEffect(() => {
    const pathFieldName = fieldMappings?.find(
      ({ targetFieldName }) => targetFieldName === 'path'
    )?.sourceFieldName;

    setImagePathFieldName(pathFieldName ?? 'Path');
  }, [fieldMappings]);

  const { getIngestUploadUrls } = useGetIngestUploadUrls();
  const [validateImageMetadataFile] = useValidateImageMetadataFileMutation();
  const { setSelectedFiles, setUploading } = useUploadContext();
  const { shouldAutoRelease, setShouldAutoRelease } = useUploadInspectionDataModalContext();

  const handleValidate = async (allImagesHaveUrls: boolean) => {
    if (file) {
      setUploading(true);

      const dataType = inspectionUploadEventTypes.inspectionType;
      const fileName = file.name;

      // Get signed url for upload
      try {
        const { items } = await getIngestUploadUrls({
          dataType,
          files: [{ fileName }],
          prefix: dataType,
          recordId: uploadId,
        });

        const [uploadItem] = items;
        if (!uploadItem) {
          message.error('Error uploading image details: Upload URL could not be obtained');
          setUploading(false);
          return;
        }

        setSelectedFiles([
          {
            name: fileName,
            path: '',
            percent: 0,
            sourceName: fileName,
            type: 'file',
            valid: true,
          },
        ]);

        // Upload file to S3
        await uploadIngestFiles({
          items: [{ ...uploadItem, file }],
        });

        // Once the file is in S3, call mutation to process
        const input: AtlasGqlValidateImageMetadataFileInput = {
          uploadId,
          ...(uploadType === UploadTypes.Single ? { inspectionId: inspectionIds[0] } : {}),
          key: uploadItem.fields.key,
          bucket: uploadItem.fields.bucket,
          fileFormat: fileType,
          decimalSeparator: decimalDelimiter,
          delimiter: cellDelimiter,
          fieldMappings,
        };

        // Set the input object in parent state to use for submitting the process mutation
        validateImageMetadataInputRef.current = input;

        // Validate the image metadata
        const { data } = await validateImageMetadataFile({
          variables: { input },
        });

        setSelectedFiles([]);

        if (data?.validateImageMetadataFile?.failureCount) {
          setUploading(false);
          setValidationStepProps({
            validateImageMetadataFile: data?.validateImageMetadataFile,
            fileName: file?.name ?? 'your details file',
            setStep,
            setErroredUploadSteps,
          });
          setStep(Steps.DetailsValidation);
          setErroredUploadSteps(uploadErrors => ({
            ...uploadErrors,
            imageDetails: true,
          }));
        } else {
          if (allImagesHaveUrls) {
            // We don't have to upload images, because they all have URLs.
            // So call the onProcessComplete callback with empty input instead
            onProcessComplete({});
          } else {
            setUploading(false);
            setStep(Steps.ImageUpload);
          }
        }
      } catch (error) {
        message.error(`Error uploading image details: ${error.message}`);
        setUploading(false);
        return;
      }
    }
  };

  const { CSVDownloader } = useCSVDownloader();
  // Declaring a styled component inside component because CSVDownloader must be desctructured from useCSVDownloader
  const StyledCSVDownloader = styled(CSVDownloader)<{ marginRight?: string }>`
    background: transparent;
    border: 0;

    ${({ marginRight }) => marginRight && `margin-right: ${marginRight}`}
  `;

  const handleBack = () => {
    setStep(
      uploadType === UploadTypes.Multiple ? Steps.SummaryValidation : Steps.SingleUploadValidation
    );
  };

  const shouldShowSkyCrawler =
    hasSkyCrawlerUploadRT &&
    hasSkyCrawlerUploadFlag &&
    inspectionIds.length === 1 &&
    [BLADE_INTERNAL].includes(inspectionType?.name ?? '');
  const shouldShowAeroEnterprise =
    hasAeroEnterpriseUploadRT &&
    hasAeroEnterpriseUploadFlag &&
    inspectionIds.length === 1 &&
    [AUTONOMOUS_DRONE, MANUAL_DRONE].includes(inspectionType?.name ?? '');

  const shouldShowRobotics = hasRoboticsUploadFlag && inspectionIds.length === 1;
  const defaultCreatePanel = (
    <div>
      {[Steps.DetailsUpload, Steps.DetailsMapping].includes(step) && (
        <>
          {existingTaskCount ? (
            <p>
              <strong>Upload details for {existingTaskCount} Inspections</strong>
            </p>
          ) : null}
          <p>
            A details file provides metadata mappings for images that will be uploaded to an
            inspection.
            {existingTaskCount
              ? ''
              : ' A details file is not required for single inspection upload, as the data can be manually entered once the images have been added.'}
          </p>
          {!existingTaskCount && !!(exampleCsv.length || exampleJson.length) && (
            <p>
              Click to download a sample details file in{' '}
              <StyledCSVDownloader
                data={exampleCsv}
                filename={'image_details_example'}
                type={'link'}
              >
                csv
              </StyledCSVDownloader>{' '}
              or{' '}
              <a download="image_details_example.json" href={exampleJson}>
                json
              </a>
              .
            </p>
          )}
          <p>
            Note: SkySpecs recommends uploading images via URL for file transfers greater than 4GB
            for better speed and stability.
          </p>
        </>
      )}
      {step === Steps.DetailsUpload && (
        <>
          <Upload
            accept={'.csv,.json'}
            beforeUpload={() => false} // bypass antd callbacks; handle change ourselves
            // @ts-ignore - tedious antd types; this is fine
            onChange={handleUpload}
          >
            <Button className="testid-upload-image-details" icon={<UploadOutlined />}>
              Upload Details
            </Button>
          </Upload>
          <br />
          {existingTaskCount ? (
            <>
              <p>
                If you do not have an image details file for the images from Inspections related to
                these Tasks, you can generate one to edit that is pre-populated with Task Ids using
                one of the options below.
                <br />
                <br />
                <StyledCSVDownloader
                  data={exampleCsv}
                  filename={'details'}
                  type={'link'}
                  marginRight={'0.5rem'}
                >
                  <Button icon={<DownloadOutlined />}>details.csv</Button>
                </StyledCSVDownloader>
                <Button icon={<DownloadOutlined />} download="details.json" href={exampleJson}>
                  details.json
                </Button>
              </p>
            </>
          ) : null}
          <ButtonContainer>
            <Button disabled={initialStep === Steps.DetailsUpload} onClick={handleBack}>
              Back
            </Button>
            {uploadType === UploadTypes.Single && (
              <Button onClick={() => setStep(Steps.ImageUpload)}>Skip</Button>
            )}
          </ButtonContainer>
        </>
      )}
      {step === Steps.DetailsMapping && (
        <>
          <Divider />
          <Row gutter={{ sm: 16 }}>
            <Col flex="50%">
              <h3>
                Selected file: <strong>{file?.name}</strong>
              </h3>
              <p>Select a source column for each Details attribute</p>
            </Col>
            <Col flex="50%">
              <Checkbox
                checked={shouldAutoRelease}
                onChange={() => setShouldAutoRelease(current => !current)}
              >
                Automatically release inspection{uploadType === UploadTypes.Multiple ? 's' : ''}{' '}
                after successful upload.{' '}
                <TooltipWithQuestionIcon
                  message={
                    'When this box is unchecked, inspections will remain in the Draft state until they are released by a user.'
                  }
                />
              </Checkbox>
            </Col>
          </Row>
          <FieldMappingForm
            fieldMappings={fieldMappings}
            fileType={fileType}
            ignoreRequired={!shouldAutoRelease}
            internalFields={internalFields}
            requireAtLeastOneOf={
              uploadType === UploadTypes.Multiple ? [['id', 'taskId']] : undefined
            }
            providedFieldNames={providedFieldNames ?? []}
            previousStep={Steps.DetailsUpload}
            nextStep={Steps.DetailsPreview}
            setStep={setStep}
            setFieldMappings={setFieldMappings}
            likelyDelimiterError={likelyDelimiterError}
            cellDelimiter={cellDelimiter ?? AtlasGqlFileFieldDelimiter.Comma}
            setCellDelimiter={setCellDelimiter}
            decimalDelimiter={decimalDelimiter}
            setDecimalDelimiter={setDecimalDelimiter}
          />
        </>
      )}
      {step === Steps.DetailsPreview && (
        <Preview
          uploadType={uploadType}
          inspectionIds={inspectionIds}
          fieldMappings={fieldMappings}
          previewData={previewData}
          setStep={setStep}
          handleComplete={handleValidate}
        />
      )}
      {step === Steps.DetailsValidation && validationStepProps && (
        <Validation {...validationStepProps} />
      )}
    </div>
  );

  const createPanels: { title: string; content: JSX.Element }[] = [
    {
      title: 'Image Detail Upload',
      content: defaultCreatePanel,
    },
    ...(shouldShowSkyCrawler
      ? [
          {
            title: 'SkyCrawler Upload',
            content: (
              <RoboticsUpload
                inspectionId={inspectionIds[0]}
                acceptedFileType=".mcap"
                outputDataType={IMAGE_AND_METADATA_UPLOAD_OPTIONS.SKYCRAWLER_LOG}
                onComplete={() => {
                  onComplete && onComplete();
                  onCloseModal();
                }}
                onBack={handleBack}
              />
            ),
          },
        ]
      : []),
    ...(shouldShowAeroEnterprise
      ? [
          {
            title: 'Aero Enterprise Upload',
            content: (
              <AeroEnterpriseUpload
                inspectionId={inspectionIds[0]}
                onComplete={() => {
                  onComplete && onComplete();
                  onCloseModal();
                }}
                onBack={handleBack}
              />
            ),
          },
        ]
      : []),
    ...(shouldShowRobotics
      ? [
          {
            title: 'Robotics Upload',
            content: (
              <RoboticsUpload
                inspectionId={inspectionIds[0]}
                acceptedFileType=".zip"
                outputDataType={IMAGE_AND_METADATA_UPLOAD_OPTIONS.ROBOTICS_UPLOAD}
                onComplete={() => {
                  onComplete && onComplete();
                  onCloseModal();
                }}
                onBack={handleBack}
              />
            ),
          },
        ]
      : []),
  ];

  return createPanels.length > 1 ? (
    <Collapse accordion={true} defaultActiveKey={0}>
      {createPanels.map((panel, index) => {
        const { title, content } = panel;
        return (
          <Collapse.Panel header={title} key={index}>
            {content}
          </Collapse.Panel>
        );
      })}
    </Collapse>
  ) : (
    defaultCreatePanel
  );
};
